import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useConfig } from "../../config";
import { Resizable } from "re-resizable";
import { ClientCopyeditPanel } from "./ClientCopyeditPanel";
const RightPanel = ({
  editor,
  children,
  isOpen,
  onClose,
  setPanelValue,
  setPanelOpen,
  panelValue,
  setMetaValue,
}) => {
  const { queryLength, referenceLength } = useSelector((state) => state.editor);
  const { config } = useConfig();
  const [isResizing, setIsResizing] = useState(false);
  // Retrieve persisted width from localStorage (fallback to 0 if not set)
  const savedWidth = Number(localStorage.getItem('rightPanelWidth')) || 0;
  const [width, setWidth] = useState(isOpen ? (savedWidth || 392) : 0);
  const [counts, setCounts] = useState({ figures: 0, tables: 0, equations: 0 });

  useEffect(() => {
    if (editor?.dom) {
      const updateCounts = () => {
        const figLen = editor.dom.select("fig").length;
        const tabLen = editor.dom.select("table-wrap").length;
        const eqLen = editor.dom.select("disp-formula, inline-formula").length;
        setCounts({ figures: figLen, tables: tabLen, equations: eqLen });
      };

      updateCounts();
      editor.on("NodeChange SetContent Change keyup", updateCounts);
      return () => {
        editor.off("NodeChange SetContent Change keyup", updateCounts);
      };
    }
  }, [editor]);

  useEffect(() => {
    if (!isResizing) {
      const defaultWidth = savedWidth > 0
        ? savedWidth
        : panelValue === "pdf" || panelValue === "copyedit" || panelValue === "qc"
          ? 520
          : 392;
      setWidth(isOpen ? defaultWidth : 0);
    }
  }, [isOpen, panelValue, isResizing]);

  const BanelButtons = [
    {
      name: "Metadata",
      value: "meta",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M9 4v16M4 9h5" />
        </svg>
      ),
      length: null,
    },
    {
      name: "Document Outline",
      value: "outline",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <line x1="21" y1="6" x2="3" y2="6" />
          <line x1="21" y1="12" x2="9" y2="12" />
          <line x1="21" y1="18" x2="7" y2="18" />
        </svg>
      ),
      length: null,
    },
    {
      name: "Author Queries",
      value: "query",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M9.5 9a1.6 1.6 0 1 1 2.3 1.6c-.5.3-.8.6-.8 1.1M11 15h.01" />
        </svg>
      ),
      length: queryLength ?? 0,
    },
    {
      name: "References",
      value: "ref",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2z" />
          <path d="M8 7h7M8 11h7" />
        </svg>
      ),
      length: referenceLength ?? 0,
    },
    {
      name: "Figures",
      value: "figures",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      ),
      length: counts.figures,
    },
    {
      name: "Tables",
      value: "tables",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3h18v18H3zM3 9h18M3 15h18M12 3v18" />
        </svg>
      ),
      length: counts.tables,
    },
    {
      name: "Equations",
      value: "equations",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19L10 5L14 15L20 5" />
        </svg>
      ),
      length: counts.equations,
    },
    {
      name: "Copyedit Review",
      value: "copyedit",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      ),
      length: null,
    },
    {
      name: "QC Dashboard",
      value: "qc",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
        </svg>
      ),
      length: null,
    },
    {
      name: "View PDF",
      value: "pdf",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 3v5h5M14 3l5 5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
          <path d="M9 13h1.5a1.2 1.2 0 0 1 0 2.4H9zm0 0v4" />
        </svg>
      ),
      length: null,
    },
  ];

  const handlePanelClick = (item) => {
    if (!isOpen) {
      setPanelValue(item.value);
      setPanelOpen(true);
    } else if (panelValue === item.value) {
      setPanelOpen(false);
      setMetaValue(null);
      setPanelValue(null);
    } else {
      setPanelValue(item.value);
    }
  };

  const headingMap = {
    meta: "Metadata",
    outline: "Table Of Contents",
    query: "Author Queries",
    ref: "References",
    pdf: "PDF Preview",
    figures: "Figures",
    tables: "Tables",
    equations: "Equations",
    copyedit: "Copyedit Review",
    qc: "QC Dashboard",
  };

  const activeHeading = headingMap[panelValue] || "";

  return (
    <Resizable
      size={{ width: isOpen ? width + 52 : 52, height: "calc(100vh - 100px)" }}
      minWidth={isOpen ? 340 : 52}
      maxWidth={window.innerWidth * 0.6}
      style={{
        transition: isResizing ? "none" : "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        borderLeft: "1px solid #E3E0D8",
        height: "calc(100vh - 100px)",
        marginTop: "100px",
        display: "flex",
      }}
      enable={{ top: false, right: false, bottom: false, left: isOpen }}
      onResizeStart={() => setIsResizing(true)}
      onResizeStop={(e, direction, ref) => {
        setIsResizing(false);
        const newWidth = ref.offsetWidth - 52;
        setWidth(newWidth);
        // Persist the new width
        localStorage.setItem('rightPanelWidth', newWidth.toString());
      }}
    >
      <div className="h-full bg-white flex w-full overflow-hidden" style={{ minWidth: "100%", width: "100%" }}>
        {/* Left Side Content of Sidebar */}
        <div
          className="h-full flex-1 flex flex-col min-w-0"
          style={{
            display: isOpen ? "flex" : "none",
            borderRight: "1px solid #E3E0D8",
            background: "#FFFFFF",
          }}
        >
          {activeHeading && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7E4DD]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <span className="text-[17px] font-semibold text-[#17171A]">{activeHeading}</span>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-[#F4F3EF] rounded-md transition-colors text-[#6B6B76] hover:text-[#17171A] flex items-center justify-center"
                title="Close Panel"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          )}
          <div className="w-full h-full flex flex-col overflow-auto p-4 right-panel-scroll">
            {children}
          </div>
          {panelValue === "copyedit" && <ClientCopyeditPanel editor={editor} />}
        </div>

        {/* Right Side Vertical Icon Rail */}
        <div
          style={{
            width: "52px",
            height: "100%",
            background: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "9px 0",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          {BanelButtons.map((item, i) => {
            const active = panelValue === item.value && isOpen;
            return (
              <button
                key={i}
                title={item.name}
                onClick={() => handlePanelClick(item)}
                style={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: active ? "#4F46E5" : "#9A988F",
                  background: active ? "#EEF0FF" : "transparent",
                  transition: "all 0.15s ease",
                  position: "relative",
                }}
                className="hover:bg-[#F4F3EF]"
              >
                {item.icon}
                {item.length > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      background: active ? "#4F46E5" : "#EF4444",
                      color: "#FFFFFF",
                      fontSize: "9px",
                      fontWeight: "700",
                      borderRadius: "8px",
                      padding: "1px 4px",
                      lineHeight: "1",
                    }}
                  >
                    {item.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Resizable>
  );
};

export default RightPanel;
